import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { APPROVAL_REFRESH_MS, approvalStatesKey } from "@/lib/approval-states";
import type { AdApprovalStatus } from "@/components/weekly-deliveries-sheet";

/** Approval polling is local to the mounted, eligible, visible My Ads page. */
export function useApprovalStates(brandId: string | undefined, enabled: boolean): Map<string, AdApprovalStatus> {
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const update = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", update);
    update();
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  const eligible = !!brandId && enabled;
  const { data } = useQuery<{ approvals: Array<{ adId: string; status: AdApprovalStatus }> }>({
    queryKey: approvalStatesKey(brandId ?? ""),
    enabled: eligible && visible,
    staleTime: APPROVAL_REFRESH_MS,
    queryFn: async () => {
      const res = await apiRequest("GET", approvalStatesKey(brandId ?? "")[0]);
      return res.json();
    },
    refetchInterval: visible && eligible ? APPROVAL_REFRESH_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  // Visibility re-enables the observer, refreshing stale/invalidated data.
  // Permission removal must not keep rendering a cached brand's badges.
  return useMemo(
    () => new Map((eligible ? data?.approvals ?? [] : []).map((a) => [a.adId, a.status])),
    [data, eligible],
  );
}
